import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from '@/components/ui/sonner';
import { adminCreateGallery, adminUpdateGallery, apiErrorMessage } from '@/lib/api';
import { GALLERY_UPLOAD } from '@/constants/testIds';
import { dialogClass, fieldClass, labelClass, goldButtonClass } from './styles';

const EMPTY_FORM = {
  slug: '', name: '', description: '', cover_image_url: '',
  session_count: 0, icon_name: 'Camera', order: 0, is_private: false, access_code: '', selection_limit: '',
};

const slugify = (value) => value.toLowerCase().replace(/\s+/g, '-');

/**
 * Create / edit a gallery. Pass `gallery` to edit (slug is locked), or null to create.
 */
export const GalleryFormDialog = ({ open, onOpenChange, gallery, onSaved }) => {
  const editing = gallery?.slug || null;
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(gallery ? { ...EMPTY_FORM, ...gallery, access_code: '', selection_limit: gallery.selection_limit ?? '' } : EMPTY_FORM);
  }, [open, gallery]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, selection_limit: form.selection_limit === '' ? null : Number(form.selection_limit) };
      if (editing) {
        const { slug: _slug, ...updates } = payload;
        await adminUpdateGallery(editing, updates);
        toast.success('Gallery updated.');
      } else {
        await adminCreateGallery(payload);
        toast.success('Gallery created.');
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to save gallery.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={dialogClass}>
        <DialogHeader>
          <DialogTitle className="text-[hsl(var(--lxra-fg))]">{editing ? 'Edit Gallery' : 'New Gallery'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSave} className="space-y-4" data-testid="admin-gallery-form">
          {!editing && (
            <div className="space-y-1.5">
              <Label className={labelClass}>Slug (URL id)</Label>
              <Input required value={form.slug} onChange={(e) => update({ slug: slugify(e.target.value) })} className={fieldClass} data-testid="admin-gallery-slug-input" />
            </div>
          )}
          <div className="space-y-1.5">
            <Label className={labelClass}>Name</Label>
            <Input required value={form.name} onChange={(e) => update({ name: e.target.value })} className={fieldClass} data-testid="admin-gallery-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label className={labelClass}>Description</Label>
            <Textarea value={form.description} onChange={(e) => update({ description: e.target.value })} className="bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))]" data-testid="admin-gallery-description-input" />
          </div>
          <div className="space-y-1.5">
            <Label className={labelClass}>Cover Image URL</Label>
            <Input required value={form.cover_image_url} onChange={(e) => update({ cover_image_url: e.target.value })} className={fieldClass} data-testid="admin-gallery-cover-input" />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-[hsl(var(--lxra-border))] p-3">
            <div>
              <Label className="text-sm text-[hsl(var(--lxra-fg))]">Private Gallery</Label>
              <p className="text-xs text-[hsl(var(--lxra-muted))]">Hidden from public grid; requires passcode or client login.</p>
            </div>
            <Switch
              checked={form.is_private}
              onCheckedChange={(v) => update({ is_private: v })}
              className="data-[state=checked]:bg-[hsl(var(--lxra-gold))]"
              data-testid="admin-gallery-private-switch"
            />
          </div>
          {form.is_private && (
            <>
              <div className="space-y-1.5">
                <Label className={labelClass}>Access Code {editing && '(leave blank to keep current)'}</Label>
                <Input value={form.access_code} onChange={(e) => update({ access_code: e.target.value })} className={fieldClass} data-testid="admin-gallery-access-code-input" />
              </div>
              <div className="space-y-1.5">
                <Label className={labelClass}>Selection Limit (optional — e.g. "choose your 20")</Label>
                <Input
                  type="number"
                  min="1"
                  placeholder="No limit"
                  value={form.selection_limit}
                  onChange={(e) => update({ selection_limit: e.target.value })}
                  data-testid={GALLERY_UPLOAD.selectionLimitInput}
                  className={fieldClass}
                />
              </div>
            </>
          )}
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="admin-gallery-save-button" className={`h-10 px-5 ${goldButtonClass}`}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? 'Save Changes' : 'Create Gallery'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
